import {
  FormHelperText,
  TextField,
  Typography,
  TypographyProps,
} from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { ChangeEvent, FC } from 'react';
import { useDictionary, useIsDesktop } from 'Hooks';
import { illegalChars } from 'Regex';
import { MAX_NOTE_LENGTH } from 'Config';

export interface INoteTextField {
  value: string;
  onChange: (value: string) => void;
  error: boolean;
  title?: string;
  placeholder?: string;
  titleVariant?: TypographyProps['variant'];
  counterColor?: string;
  errorMessage?: string;
  onBlur?: () => void;
  id?: string;
  maxLength?: number;
  rows?: number;
}

export const NoteTextField: FC<INoteTextField> = ({
  value,
  onChange,
  error,
  title,
  placeholder,
  titleVariant,
  counterColor,
  errorMessage,
  onBlur,
  id = 'request-note',
  maxLength = MAX_NOTE_LENGTH,
  rows = 4,
}) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);
  const isDesktop = useIsDesktop();
  const resolvedCounterColor = error ? '#ec0000' : counterColor || '#A020FF';

  return (
    <>
      <Typography
        variant={titleVariant || 'body3'}
        component='label'
        htmlFor={id}
      >
        {title || dictionary.uploadFile.note}
      </Typography>
      <TextField
        id={id}
        aria-describedby={error ? `${id}-error` : undefined}
        multiline
        rows={rows}
        variant='outlined'
        placeholder={placeholder || dictionary.uploadFile.notePlaceholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onInput={(e) => {
          const target = e.target as HTMLInputElement | HTMLTextAreaElement;
          target.value = target.value.replace(illegalChars, '');
        }}
        inputProps={{
          maxLength,
        }}
        error={error}
        helperText={`${value.length}/${maxLength}`}
        fullWidth
        margin='normal'
        required
        InputProps={{
          style: {
            background: '#fff',
            border: error ? '1px solid #ec0000' : '1px solid #B7CCFF',
            borderRadius: '8px',
            paddingBottom: '30px',
          },
          'aria-describedby': undefined,
        }}
        FormHelperTextProps={{
          style: {
            color: resolvedCounterColor,
            textAlign: 'left',
            fontWeight: value.length === maxLength ? 'bold' : undefined,
          },
          'aria-hidden': true,
        }}
        sx={{
          '& .MuiInputBase-input': {
            zIndex: 9,
            paddingLeft: '1px',
          },
          '& .MuiFormHelperText-root': {
            position: 'relative',
            top: isDesktop ? '-36px' : '-30px',
            left: '10px',
            fontSize: isDesktop ? '18px' : '14px',
            zIndex: 0,
          },
          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
          '.MuiInputBase-input': {
            marginBottom: '10px',
          },
        }}
        slotProps={{
          input: {
            style: {
              background: '#fff',
              border: error ? '1px solid #ec0000' : '1px solid #B7CCFF',
              borderRadius: '8px',
            },
            'aria-describedby': undefined,
          },

          htmlInput: {
            maxLength,
          },

          formHelperText: {
            style: {
              color: resolvedCounterColor,
              textAlign: 'left',
              fontWeight: value.length === maxLength ? 'bold' : undefined,
            },
            'aria-hidden': true,
          },
        }}
      />
      {error && (
        <FormHelperText
          role='alert'
          error={error}
          id={`${id}-error`}
          sx={{ m: '-38px 0 18px', fontSize: '14px', textAlign: 'right' }}
        >
          {errorMessage || dictionary.uploadFile.noteErrorMsg}
        </FormHelperText>
      )}
    </>
  );
};
